import { useState, useMemo } from 'react';
import {
  Stack,
  Paper,
  Table,
  Text,
  Group,
  TextInput,
  Skeleton,
  Badge,
} from '@mantine/core';
import { IconSearch, IconTag } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useCustomerPriceList } from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

interface PriceListItem {
  id: number;
  product_name: string;
  custom_name: string;
  unit_price: string;
  effective_date: string;
}

type SortField = 'product_name' | 'custom_name' | 'unit_price' | 'effective_date';

export default function MyPriceList() {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('product_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { data, isLoading } = useCustomerPriceList();
  const items: PriceListItem[] = (data?.items ?? []) as PriceListItem[];

  const filtered = useMemo(() => {
    let result = [...items];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.product_name.toLowerCase().includes(s) ||
          r.custom_name.toLowerCase().includes(s) ||
          r.unit_price.toLowerCase().includes(s),
      );
    }
    result.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortField];
      const bv = (b as unknown as Record<string, unknown>)[sortField];
      const cmp = (av as string) < (bv as string) ? -1 : (av as string) > (bv as string) ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [items, search, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : '';

  return (
    <Stack gap="lg">
      <PageHeader
        title="My Price List"
        description="Your personalized catalog with custom descriptions and pricing"
      />

      <TextInput
        placeholder="Search by product or custom name..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      {isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<IconTag size={30} />}
          title="No price list items"
          description={
            search
              ? 'No items match your search criteria'
              : 'Your personalized price list is not yet available'
          }
        />
      ) : (
        <Paper>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('product_name')}
                >
                  Product Name{sortIndicator('product_name')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('custom_name')}
                >
                  Custom Name{sortIndicator('custom_name')}
                </Table.Th>
                <Table.Th
                  ta="right"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('unit_price')}
                >
                  Unit Price{sortIndicator('unit_price')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('effective_date')}
                >
                  Effective Date{sortIndicator('effective_date')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((row) => {
                const isRecent = dayjs(row.effective_date).isAfter(
                  dayjs().subtract(7, 'day'),
                );
                return (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {row.product_name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Text size="sm" c="dimmed">
                          {row.custom_name || '\u2014'}
                        </Text>
                        {row.custom_name && row.custom_name !== row.product_name && (
                          <Badge size="xs" variant="light" color="blue">
                            custom
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" fw={500}>
                        ${parseFloat(row.unit_price).toFixed(2)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Text size="sm">
                          {dayjs(row.effective_date).format('MMM D, YYYY')}
                        </Text>
                        {isRecent && (
                          <Badge size="xs" variant="light" color="green">
                            new
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
